import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TwilioTemplates } from "@/components/TwilioTemplates";
import { toast } from "sonner";
import { Building2, MessageCircle, Mail, ReceiptText, Search, Bot, CheckCircle2, XCircle, Send, MapPin, SatelliteDish } from "lucide-react";
import { IndonesiaMap } from "@/components/IndonesiaMap";
import { InternalTeamList } from "@/components/InternalTeamList";

const F = ["name", "tagline", "address", "phone", "email", "website", "bank_info", "signer", "director_email"];
const LABELS = {
  name: "Nama Perusahaan", tagline: "Tagline", address: "Alamat", phone: "Telepon",
  email: "Email", website: "Website", bank_info: "Info Bank", signer: "Penandatangan", director_email: "Email Direktur (penerima approval)",
};

const INTEG = [
  { key: "email", label: "Email (Zoho SMTP)", icon: Mail },
  { key: "zoho_billing", label: "Zoho Billing", icon: ReceiptText },
  { key: "whatsapp", label: "WhatsApp (CallMeBot)", icon: MessageCircle },
  { key: "telegram", label: "Telegram Bot", icon: SatelliteDish },
  { key: "telegram_customer_bot", label: "Telegram Bot Customer", icon: SatelliteDish },
  { key: "whatsapp_customer", label: "WhatsApp Customer (Twilio)", icon: MessageCircle },
  { key: "tavily", label: "Pencarian Harga (Tavily)", icon: Search },
  { key: "ai", label: "AI (Gemini 3.1 Pro)", icon: Bot },
];

export default function Settings() {
  const [company, setCompany] = useState(Object.fromEntries(F.map((k) => [k, ""])));
  const [wa, setWa] = useState({ admin_phone: "", callmebot_apikey: "", callmebot_key_set: false, callmebot_key_last4: "", twilio_quote_sid: "", twilio_invoice_sid: "", auto_customer: true, director_phone: "", business_from: "", enabled: false });
  const [tg, setTg] = useState({ bot_token: "", chat_id: "", bot_token_set: false, bot_token_last4: "", customer_bot: false, bot_username: "" });
  const [integrations, setIntegrations] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tgBusy, setTgBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setCompany({ ...Object.fromEntries(F.map((k) => [k, ""])), ...data.company });
      setWa({ admin_phone: data.whatsapp.admin_phone || "", callmebot_apikey: "", callmebot_key_set: data.whatsapp.callmebot_key_set, callmebot_key_last4: data.whatsapp.callmebot_key_last4,
        twilio_quote_sid: data.whatsapp.twilio_quote_sid || "", twilio_invoice_sid: data.whatsapp.twilio_invoice_sid || "", auto_customer: data.whatsapp.auto_customer ?? true, director_phone: data.whatsapp.director_phone || "", business_from: data.whatsapp.business_from || "", enabled: !!data.whatsapp.enabled });
      setTg({ bot_token: "", chat_id: data.telegram?.chat_id || "", bot_token_set: data.telegram?.bot_token_set, bot_token_last4: data.telegram?.bot_token_last4,
        customer_bot: !!data.telegram?.customer_bot, bot_username: data.telegram?.bot_username || "" });
      setIntegrations(data.integrations || {});
    } catch (e) { toast.error("Gagal memuat pengaturan"); }
  };
  useEffect(() => { load(); }, []);

  const persist = async () => {
    const { data } = await api.put("/settings", { company, whatsapp: { admin_phone: wa.admin_phone, callmebot_apikey: wa.callmebot_apikey, twilio_quote_sid: wa.twilio_quote_sid, twilio_invoice_sid: wa.twilio_invoice_sid, auto_customer: wa.auto_customer, director_phone: wa.director_phone, business_from: wa.business_from, enabled: wa.enabled }, telegram: { bot_token: tg.bot_token, chat_id: tg.chat_id } });
    setIntegrations(data.integrations || {});
    setWa((w) => ({ ...w, callmebot_apikey: "", callmebot_key_set: data.whatsapp.callmebot_key_set, callmebot_key_last4: data.whatsapp.callmebot_key_last4 }));
    setTg((t) => ({ ...t, bot_token: "", chat_id: data.telegram?.chat_id || t.chat_id, bot_token_set: data.telegram?.bot_token_set, bot_token_last4: data.telegram?.bot_token_last4 }));
    return data;
  };

  const save = async () => {
    setBusy(true);
    try {
      await persist();
      toast.success("Pengaturan tersimpan");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testWa = async () => {
    setTesting(true);
    try {
      await persist(); // simpan dulu agar nomor & key terbaru dipakai
      await api.post("/settings/test-whatsapp");
      toast.success("Pesan tes terkirim ke WhatsApp admin");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const detectChat = async () => {
    setTgBusy(true);
    try {
      await persist();
      const { data } = await api.post("/settings/telegram-detect-chat");
      setTg((t) => ({ ...t, chat_id: data.chat_id }));
      toast.success(`Chat ID terdeteksi: ${data.chat_id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTgBusy(false); }
  };

  const toggleCustomerBot = async (on) => {
    setTgBusy(true);
    try {
      await persist();
      if (on) {
        const { data } = await api.post("/settings/telegram/connect-customer-bot");
        toast.success(`Bot @${data.bot_username} tersambung ke Tim AI Agent`);
      } else {
        await api.post("/settings/telegram/disconnect-customer-bot");
        toast.success("Bot customer diputus");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTgBusy(false); }
  };

  const testTg = async () => {
    setTgBusy(true);
    try {
      await persist();
      await api.post("/settings/test-telegram");
      toast.success("Pesan tes terkirim ke Telegram admin");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTgBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Konfigurasi" title="Company Profile">
        <Button className="rounded-sm" onClick={save} disabled={busy} data-testid="settings-save-btn">
          {busy ? "Menyimpan…" : "Simpan Perubahan"}
        </Button>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        <div className="lg:col-span-3 border border-border bg-white rounded-sm p-4" data-testid="company-map">
          <div className="flex items-center gap-2 mb-2 px-2">
            <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/50" /><MapPin className="w-3.5 h-3.5 text-accent" /></span>
            <span className="text-sm font-medium">Jaringan Cabang — Kantor Pusat: <span className="text-accent font-semibold">Medan</span> · Area Support Team</span>
          </div>
          <div className="max-w-3xl mx-auto"><IndonesiaMap /></div>
        </div>
        {/* Company info */}
        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <Building2 className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold">Informasi Perusahaan</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {F.map((k) => (
              <div key={k} className={k === "address" || k === "bank_info" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{LABELS[k]}</Label>
                <Input data-testid={`company-${k}`} value={company[k] || ""} onChange={(e) => setCompany({ ...company, [k]: e.target.value })} className="mt-1 rounded-sm bg-secondary" />
              </div>
            ))}
            <div>
              <Label className="text-xs">Masa Berlaku Penawaran (hari)</Label>
              <Input data-testid="company-quote-validity" type="number" min={1} max={365} value={company.quote_validity_days ?? 14}
                onChange={(e) => setCompany({ ...company, quote_validity_days: Number(e.target.value) || 14 })} className="mt-1 rounded-sm bg-secondary" />
              <p className="text-[11px] text-muted-foreground mt-1">Dihitung sejak penawaran dikirim. Pengingat H-1 dikirim via email & WA, lalu otomatis ditandai kedaluwarsa.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4 mt-8">
            <MessageCircle className="w-5 h-5 text-accent" />
            <h3 className="font-heading font-bold">WhatsApp Admin (CallMeBot)</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Nomor WhatsApp Admin</Label>
              <Input data-testid="wa-phone" value={wa.admin_phone} onChange={(e) => setWa({ ...wa, admin_phone: e.target.value })} placeholder="cth. 6281234567890" className="mt-1 rounded-sm bg-secondary" />
            </div>
            <div>
              <Label className="text-xs">API Key CallMeBot {wa.callmebot_key_set && <span className="text-accent">(tersimpan •••{wa.callmebot_key_last4})</span>}</Label>
              <Input data-testid="wa-key" value={wa.callmebot_apikey} onChange={(e) => setWa({ ...wa, callmebot_apikey: e.target.value })} placeholder={wa.callmebot_key_set ? "Biarkan kosong untuk mempertahankan" : "Masukkan API key numerik"} className="mt-1 rounded-sm bg-secondary" />
            </div>
          </div>
          <Button variant="outline" className="rounded-sm mt-4" onClick={testWa} disabled={testing} data-testid="wa-test-btn">
            <Send className="w-4 h-4 mr-2" /> {testing ? "Mengirim…" : "Kirim Pesan Tes"}
          </Button>
          <div className="mt-4 p-3 rounded-sm bg-secondary/60 border border-border text-xs text-muted-foreground leading-relaxed" data-testid="callmebot-help">
            <b className="text-foreground">Cara dapat API Key CallMeBot:</b>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Simpan nomor <b className="text-foreground">+34 623 78 64 49</b> (kontak muncul sebagai <b>CallMeBot</b>).</li>
              <li>Kirim pesan persis: <code className="bg-background px-1 rounded">I allow callmebot to send me messages</code></li>
              <li>Tunggu balasan otomatis berisi API key (angka). <b>Jangan mengetik angka apa pun.</b></li>
            </ol>
            <p className="mt-1">Lupa key? Kirim <code className="bg-background px-1 rounded">Recover APIKey</code> ke bot tsb.</p>
          </div>

          <div className="flex items-center gap-2 mb-4 mt-8">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold">WhatsApp Customer (Twilio)</h3>
            <span className="ml-auto text-xs text-muted-foreground">{wa.enabled ? "Aktif" : "Nonaktif"}</span>
            <Switch data-testid="wa-enabled-switch" checked={wa.enabled} onCheckedChange={(v) => setWa({ ...wa, enabled: v })} />
          </div>
          {!wa.enabled && <p className="text-xs text-muted-foreground -mt-2" data-testid="wa-disabled-note">WhatsApp dinonaktifkan. Pengaturan Twilio tetap tersimpan dan bisa diaktifkan kembali kapan saja.</p>}
          {wa.enabled && (<>
          <div className="flex items-center justify-between gap-3 p-3 border border-border rounded-sm mb-4">
            <div className="text-sm">
              <div className="font-medium">Kirim otomatis setelah disetujui</div>
              <div className="text-xs text-muted-foreground">Penawaran terkirim saat admin menyetujui, invoice terkirim saat diterbitkan.</div>
            </div>
            <Switch data-testid="wa-auto-customer-switch" checked={wa.auto_customer} onCheckedChange={(v) => setWa({ ...wa, auto_customer: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Template Penawaran Twilio (ContentSid)</Label>
              <Input data-testid="wa-quote-sid" value={wa.twilio_quote_sid} onChange={(e) => setWa({ ...wa, twilio_quote_sid: e.target.value })} placeholder="HX..." className="mt-1 rounded-sm bg-secondary font-mono" />
            </div>
            <div>
              <Label className="text-xs">Template Invoice Twilio (ContentSid)</Label>
              <Input data-testid="wa-invoice-sid" value={wa.twilio_invoice_sid} onChange={(e) => setWa({ ...wa, twilio_invoice_sid: e.target.value })} placeholder="HX..." className="mt-1 rounded-sm bg-secondary font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-xs">No. WhatsApp Bisnis (pengirim Twilio)</Label>
              <Input data-testid="wa-business-from" value={wa.business_from} onChange={(e) => setWa({ ...wa, business_from: e.target.value })} placeholder="+62... — harus sudah terdaftar di Twilio" className="mt-1 rounded-sm bg-secondary" />
            </div>
            <div>
              <Label className="text-xs">No. WhatsApp Direktur (Ongah Hermanto)</Label>
              <Input data-testid="wa-director-phone" value={wa.director_phone} onChange={(e) => setWa({ ...wa, director_phone: e.target.value })} placeholder="62812xxxx — bukan nomor bisnis Twilio" className="mt-1 rounded-sm bg-secondary" />
              {wa.director_phone && wa.business_from && wa.director_phone.replace(/\D/g, "").replace(/^0/, "62") === wa.business_from.replace(/\D/g, "").replace(/^0/, "62") && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="wa-director-conflict">Sama dengan nomor bisnis — approval WA belum bisa dikirim. Ganti nomor bisnis di Twilio terlebih dahulu.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Webhook pesan masuk (tempel di Twilio)</Label>
              <Input readOnly data-testid="wa-webhook-url" value={`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`} className="mt-1 rounded-sm bg-secondary font-mono text-xs" onFocus={(e) => e.target.select()} />
            </div>
          </div>
          <div className="mt-4 p-3 rounded-sm bg-secondary/60 border border-border text-xs text-muted-foreground leading-relaxed" data-testid="twilio-help">
            <b className="text-foreground">Variabel template yang dikirim:</b>
            <ul className="list-disc ml-4 mt-1 space-y-0.5">
              <li><code className="bg-background px-1 rounded">{"{{1}}"}</code> Nama customer · <code className="bg-background px-1 rounded">{"{{2}}"}</code> Nomor dokumen</li>
              <li><code className="bg-background px-1 rounded">{"{{3}}"}</code> Total (Rp) · <code className="bg-background px-1 rounded">{"{{4}}"}</code> Link PDF</li>
            </ul>
            <p className="mt-1">Contoh: <i>Halo {"{{1}}"}, berikut dokumen {"{{2}}"} senilai {"{{3}}"}. Unduh: {"{{4}}"}</i>. Tanpa ContentSid, pesan hanya terkirim jika customer menghubungi Anda dalam 24 jam terakhir.</p>
          </div>

          <TwilioTemplates />
          </>)}

          <div className="flex items-center gap-2 mb-4 mt-8">
            <SatelliteDish className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold">Telegram Bot <span className="text-xs font-normal text-accent">(chat customer dengan Tim AI Agent)</span></h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Bot Token {tg.bot_token_set && <span className="text-accent">(tersimpan •••{tg.bot_token_last4})</span>}</Label>
              <Input data-testid="tg-token" value={tg.bot_token} onChange={(e) => setTg({ ...tg, bot_token: e.target.value })} placeholder={tg.bot_token_set ? "Biarkan kosong untuk mempertahankan" : "123456:ABC-DEF..."} className="mt-1 rounded-sm bg-secondary" />
            </div>
            <div>
              <Label className="text-xs">Chat ID Admin</Label>
              <Input data-testid="tg-chat" value={tg.chat_id} onChange={(e) => setTg({ ...tg, chat_id: e.target.value })} placeholder="otomatis via tombol Deteksi" className="mt-1 rounded-sm bg-secondary" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" className="rounded-sm" onClick={detectChat} disabled={tgBusy} data-testid="tg-detect-btn">
              <SatelliteDish className="w-4 h-4 mr-2" /> Deteksi Chat ID
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={testTg} disabled={tgBusy} data-testid="tg-test-btn">
              <Send className="w-4 h-4 mr-2" /> {tgBusy ? "Memproses…" : "Kirim Pesan Tes"}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 p-3 border border-border rounded-sm" data-testid="tg-customer-bot">
            <div className="text-sm">
              <div className="font-medium">Bot untuk customer {tg.customer_bot && tg.bot_username && (
                <a href={`https://t.me/${tg.bot_username}`} target="_blank" rel="noreferrer" className="text-primary underline ml-1" data-testid="tg-bot-link">t.me/{tg.bot_username}</a>)}</div>
              <div className="text-xs text-muted-foreground">Customer chat ke bot → Nadia menjawab, tim agent memproses penawaran & invoice, kabar dikirim di chat yang sama.</div>
            </div>
            <Switch data-testid="tg-customer-bot-switch" checked={tg.customer_bot} disabled={tgBusy} onCheckedChange={toggleCustomerBot} />
          </div>
          <InternalTeamList />
          <div className="mt-4 p-3 rounded-sm bg-secondary/60 border border-border text-xs text-muted-foreground leading-relaxed" data-testid="telegram-help">
            <b className="text-foreground">Cara pasang Bot Telegram:</b>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Di Telegram, chat <b className="text-foreground">@BotFather</b> → kirim <code className="bg-background px-1 rounded">/newbot</code> → ikuti langkah → salin <b>Bot Token</b>.</li>
              <li>Tempel Bot Token di atas, klik <b>Simpan Perubahan</b>.</li>
              <li>Buka bot Anda (link dari BotFather), kirim <code className="bg-background px-1 rounded">/start</code>, lalu klik <b>Deteksi Chat ID</b>.</li>
              <li>Klik <b>Kirim Pesan Tes</b> untuk verifikasi notifikasi admin.</li>
              <li>Nyalakan <b>Bot untuk customer</b>, lalu bagikan link t.me bot ke customer (website, kartu nama, email). Deteksi Chat ID dilakukan <b>sebelum</b> menyalakan bot customer.</li>
            </ol>
          </div>
        </div>

        {/* Integration status */}
        <div className="border border-border bg-card rounded-sm p-6 h-fit">
          <h3 className="font-heading font-bold mb-4">Status Integrasi</h3>
          <div className="space-y-3">
            {INTEG.map(({ key, label, icon: Icon }) => {
              const ok = integrations[key];
              return (
                <div key={key} className="flex items-center justify-between text-sm" data-testid={`integ-${key}`}>
                  <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /> {label}</span>
                  {ok ? (
                    <span className="flex items-center gap-1 text-accent text-xs font-medium"><CheckCircle2 className="w-4 h-4" /> Aktif</span>
                  ) : (
                    <span className="flex items-center gap-1 text-muted-foreground text-xs"><XCircle className="w-4 h-4" /> Belum</span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            Key rahasia lain (Zoho, SMTP, Tavily) dikelola aman di server. Halaman ini menampilkan statusnya.
          </p>
        </div>
      </div>
    </div>
  );
}
